import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

export default function CGV() {
  const { language } = useLanguage();
  return (
    <div className="min-h-screen pt-20 lg:pt-24" data-testid="legal-page">
      <SEO
        title="Conditions générales de vente - AfriOne BD"
        description="Conditions générales de vente d'AfriOne BD : commande, livraison, paiement, droit de rétractation et réclamations."
        canonical="https://afrionebd.eu/cgv"
      />
      <section className="py-12 lg:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="text-xs uppercase tracking-widest text-primary font-medium">Vos droits & nos engagements</span>
            <h1 className="text-3xl lg:text-4xl font-bold font-heading text-text_primary mt-2 mb-2 tracking-tight">
              Conditions Générales de Vente
            </h1>

            {/* Document juridique : il fait foi en français. Une traduction
                de complaisance créerait une ambiguïté sur le texte applicable. */}
            {language === 'en' && (
              <p className="mb-6 p-4 border border-border bg-secondary/20 text-sm text-text_secondary">
                These terms of sale are available in French only. The French version is the
                legally binding text. For any question, write to{' '}
                <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline">contact@afrionebd.eu</a>.
              </p>
            )}
            <p className="text-sm text-text_secondary mb-10">Dernière mise à jour : juillet 2026</p>

            <div className="space-y-8 text-text_secondary leading-relaxed">
              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">1. Objet et champ d'application</h2>
                <p>
                  Les présentes conditions générales de vente (CGV) régissent les ventes de produits
                  alimentaires proposés par <strong>AfriOne BD</strong> (Rue Saint-Nicolas 227, 4000 Liège,
                  BCE 1031.014.285) via le site afrionebd.eu. Toute commande implique l'acceptation pleine
                  et entière des présentes CGV.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">2. Produits et prix</h2>
                <p>
                  Les produits proposés sont décrits et présentés avec la plus grande exactitude possible.
                  Les prix sont indiqués en euros (€), toutes taxes comprises (TTC), hors frais de livraison.
                  AfriOne BD se réserve le droit de modifier ses prix à tout moment ; les produits sont
                  facturés au tarif en vigueur au moment de la validation de la commande, dans la limite des
                  stocks disponibles.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">3. Commande</h2>
                <p>
                  Le client sélectionne ses produits, renseigne ses coordonnées et son adresse de livraison,
                  puis valide sa commande. Un email de confirmation récapitulant la commande lui est envoyé.
                  AfriOne BD se réserve le droit d'annuler ou de refuser toute commande en cas de litige
                  antérieur, de commande anormale ou d'indisponibilité des produits.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">4. Livraison ou retrait en magasin</h2>
                <p>
                  Le client choisit, au moment de la commande, entre la livraison à domicile et le
                  retrait en magasin.
                </p>
                <p className="mt-3">
                  <strong>Retrait en magasin</strong> — la commande est mise à disposition chez
                  AfriOne BD, Rue St Nicolas 227, 4000 Liège, aux heures d'ouverture. Ce mode est
                  gratuit et sans montant minimum de commande. La commande peut être retirée
                  immédiatement après sa validation, pendant les heures d'ouverture. Le client
                  dispose de sept jours pour venir la chercher ; passé ce délai, les produits
                  frais ou surgelés ne peuvent plus être garantis.
                </p>
                <p className="mt-3">
                  <strong>Livraison</strong> — elle s'effectue à l'adresse indiquée par le client,
                  selon les zones et tarifs suivants :
                </p>
                <ul className="mt-3 space-y-1">
                  <li>• <strong>Liège</strong> : 5,90 €</li>
                  <li>• <strong>Périphérie de Liège</strong> (Seraing, Herstal, Ans, Flémalle, etc.) : 7,90 €</li>
                  <li>• <strong>Hors Liège</strong> : 14,90 € (commande minimum de 75 €)</li>
                </ul>
                <p className="mt-3">
                  Les délais de livraison sont communiqués à titre indicatif. AfriOne BD ne saurait être
                  tenue responsable des retards dus à des circonstances indépendantes de sa volonté. En cas
                  d'absence du client au moment de la livraison, celui-ci sera recontacté pour convenir d'une
                  nouvelle présentation.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">5. Paiement</h2>
                <p>
                  Deux modes de paiement sont proposés : <strong>en ligne</strong> par Bancontact ou carte
                  bancaire (Visa, Mastercard), via notre prestataire de paiement sécurisé Mollie, ou
                  <strong> en espèces à la livraison ou lors du retrait en magasin</strong>. AfriOne BD n'a jamais accès aux données de
                  votre carte bancaire. La commande n'est considérée comme définitive qu'après paiement
                  effectif ou, pour le paiement à la livraison ou au retrait, après confirmation
                  de la commande.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">6. Droit de rétractation</h2>
                <p>
                  Conformément au Code de droit économique belge (art. VI.53), le droit de rétractation de
                  14 jours <strong>ne s'applique pas</strong> à la fourniture de biens susceptibles de se
                  détériorer ou de se périmer rapidement (produits frais, surgelés, denrées alimentaires),
                  ni aux biens scellés ne pouvant être renvoyés pour des raisons d'hygiène et descellés après
                  livraison.
                </p>
                <p className="mt-3">
                  Pour les rares produits non périssables et non descellés, le client dispose d'un délai de
                  14 jours pour exercer son droit de rétractation, sans avoir à motiver sa décision, en nous
                  contactant à <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline">contact@afrionebd.eu</a>.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">7. Réclamations et produits non conformes</h2>
                <p>
                  En cas de produit manquant, endommagé ou non conforme, le client est invité à nous
                  contacter dans les <strong>24 heures</strong> suivant la livraison à
                  <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline"> contact@afrionebd.eu</a>
                  ou au 0465 71 01 17, en fournissant si possible une photo. Un remboursement ou un
                  remplacement sera proposé selon la situation.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">8. Service client</h2>
                <p>
                  Pour toute question relative à une commande : <a href="mailto:contact@afrionebd.eu" className="text-primary hover:underline">contact@afrionebd.eu</a> - 0465 71 01 17.
                </p>
              </div>

              <div>
                <h2 className="text-xl font-bold text-text_primary mb-3">9. Droit applicable et litiges</h2>
                <p>
                  Les présentes CGV sont soumises au droit belge. En cas de litige, une solution amiable sera
                  recherchée en priorité. À défaut, les tribunaux de l'arrondissement judiciaire de Liège
                  seront compétents. Le consommateur peut également recourir à la plateforme européenne de
                  règlement en ligne des litiges :
                  <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline"> ec.europa.eu/consumers/odr</a>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
